import { supabase } from "../config/supabase";

/**
 * Calcola in quale settimana del ciclo ci troviamo
 * @param rotationStartDate Data di inizio della rotazione (settimana 1)
 * @param currentDate Data corrente (default: oggi)
 * @returns Numero della settimana (1, 2, 3, o 4)
 */
export const getCurrentWeekNumber = (
  rotationStartDate: Date,
  currentDate: Date = new Date()
): number => {
  // Calcola la differenza in giorni tra la data corrente e la data di inizio
  const timeDiff = currentDate.getTime() - rotationStartDate.getTime();
  const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

  // Calcola il numero della settimana (1-4) in base a giorni passati dal lunedì di inizio
  const weekNumber = Math.floor(daysDiff / 7) % 4;

  // Restituisce sempre un numero tra 1 e 4
  return weekNumber === 0 ? 4 : weekNumber;
};

/**
 * Ottiene la data di inizio della rotazione dalla configurazione Supabase
 */
export const getRotationStartDate = async (): Promise<Date> => {
  try {
    const { data, error } = await supabase
      .from("settings")
      .select("rotation_start_date")
      .single();

    if (error || !data) {
      // Se non c'è configurazione, usa una data di default
      // Come esempio, lunedì 10 gennaio 2024
      return new Date("2024-01-10");
    }

    return new Date(data.rotation_start_date);
  } catch (error) {
    console.error("Errore nel recupero della data di inizio:", error);
    return new Date("2024-01-10"); // Data di default
  }
};

/**
 * Converte il numero del giorno della settimana in nome del giorno
 * @param dayOfWeek 1 = Lunedì, 2 = Martedì, ..., 5 = Venerdì
 */
export const getDayName = (dayOfWeek: number): string => {
  const dayNames = ["", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì"];
  return dayNames[dayOfWeek] || "";
};

/**
 * Ottiene il giorno della settimana corrente (1-5, lunedì-venerdì)
 */
export const getCurrentDayOfWeek = (date: Date = new Date()): number => {
  const dayOfWeek = date.getDay(); // 0 = Domenica, 1 = Lunedì, ...

  // Converte in formato 1-5 (lunedì-venerdì)
  if (dayOfWeek === 0) return 5; // Se è domenica, usa 5 (venerdì)
  if (dayOfWeek >= 6) return 5; // Se è sabato o altro, usa venerdì
  return dayOfWeek;
};

/**
 * Formatta una data in formato italiano
 */
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString("it-IT", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Calcola tutte le settimane del ciclo (1-4) per creare le opzioni del selettore
 */
export const getAvailableWeeks = (): string[] => {
  return ["Settimana 1", "Settimana 2", "Settimana 3", "Settimana 4"];
};

/**
 * Formatta la settimana selezionata in formato più user-friendly
 */
export const formatWeekSelection = (weekNumber: number): string => {
  return `Settimana ${weekNumber}`;
};
